<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';

import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import StudioSection from '../components/StudioSection.vue';
import StudioBadge from '../components/StudioBadge.vue';
import StudioEmptyState from '../components/StudioEmptyState.vue';

import ApiRuleForm from '../components/ApiRuleForm.vue';
import ApiRuleTestPanel from '../components/ApiRuleTestPanel.vue';

const props = defineProps({
  bot: {
    type: Object,
    required: true,
  },
});

const { t } = useI18n();
const store = useStore();

const records = useMapGetter('aiBotApiRules/getRecords');
const uiFlags = useMapGetter('aiBotApiRules/getUIFlags');

const formDialogRef = ref(null);
const testDialogRef = ref(null);
const deleteDialogRef = ref(null);
const editingRule = ref(null);
const testingRule = ref(null);
const deletingRule = ref(null);

const sortedRecords = computed(() =>
  [...records.value].sort((a, b) => (b.priority ?? 0) - (a.priority ?? 0))
);

const fetchRules = () => {
  store.dispatch('aiBotApiRules/get', { botId: props.bot.id });
};

onMounted(fetchRules);
watch(() => props.bot.id, fetchRules);

const openCreateDialog = () => {
  editingRule.value = null;
  formDialogRef.value.open();
};

const openEditDialog = rule => {
  editingRule.value = rule;
  formDialogRef.value.open();
};

const openTestDialog = rule => {
  testingRule.value = rule;
  testDialogRef.value.open();
};

const openDeleteDialog = rule => {
  deletingRule.value = rule;
  deleteDialogRef.value.open();
};

const saveRule = async payload => {
  const action = editingRule.value
    ? store.dispatch('aiBotApiRules/update', {
        botId: props.bot.id,
        id: editingRule.value.id,
        ...payload,
      })
    : store.dispatch('aiBotApiRules/create', {
        botId: props.bot.id,
        ...payload,
      });
  const saved = await action;
  if (saved) {
    useAlert(t('AI_AGENT.API_RULES.SAVED'));
    formDialogRef.value.close();
  }
};

const toggleEnabled = rule => {
  store.dispatch('aiBotApiRules/update', {
    botId: props.bot.id,
    id: rule.id,
    enabled: !rule.enabled,
  });
};

const confirmDelete = async () => {
  const deleted = await store.dispatch('aiBotApiRules/delete', {
    botId: props.bot.id,
    id: deletingRule.value.id,
  });
  if (deleted) {
    useAlert(t('AI_AGENT.API_RULES.DELETED'));
    deleteDialogRef.value.close();
  }
};

const METHOD_TONES = {
  GET: 'teal',
  POST: 'blue',
  PUT: 'amber',
  PATCH: 'amber',
  DELETE: 'ruby',
};

const methodTone = method =>
  METHOD_TONES[(method || '').toUpperCase()] || 'slate';
</script>

<template>
  <div class="flex flex-col gap-4">
    <StudioSection
      :title="$t('AI_AGENT.API_RULES.LIST_TITLE')"
      :description="$t('AI_AGENT.API_RULES.DESCRIPTION')"
      icon="i-lucide-webhook"
    >
      <template #actions>
        <Button
          icon="i-lucide-plus"
          sm
          :label="$t('AI_AGENT.API_RULES.ADD')"
          data-testid="add-rule"
          @click="openCreateDialog"
        />
      </template>

      <StudioEmptyState
        v-if="!sortedRecords.length && !uiFlags.isFetching"
        icon="i-lucide-webhook"
        :title="$t('AI_AGENT.API_RULES.EMPTY')"
        :description="$t('AI_AGENT.API_RULES.EMPTY_HELP')"
      >
        <Button
          icon="i-lucide-plus"
          sm
          :label="$t('AI_AGENT.API_RULES.ADD')"
          @click="openCreateDialog"
        />
      </StudioEmptyState>

      <ul v-else class="flex flex-col gap-2" data-testid="rules-table">
        <li
          v-for="rule in sortedRecords"
          :key="rule.id"
          class="flex flex-col gap-3 p-3 transition-colors group rounded-xl bg-n-alpha-1 outline outline-1 -outline-offset-1 outline-n-weak hover:outline-n-strong md:flex-row md:items-center motion-reduce:transition-none"
          :class="{ 'opacity-60': !rule.enabled }"
          :data-testid="`rule-row-${rule.id}`"
        >
          <div class="flex flex-col flex-1 min-w-0 gap-1.5">
            <div class="flex flex-wrap items-center gap-2">
              <span class="truncate text-heading-3 text-n-slate-12">
                {{ rule.name }}
              </span>
              <StudioBadge v-if="rule.intent" tone="blue">
                <span class="i-lucide-brain size-3" />
                {{ rule.intent }}
              </StudioBadge>
              <StudioBadge :tone="rule.hit_count ? 'blue' : 'slate'">
                <span class="i-lucide-zap size-3" />
                {{ $t('AI_AGENT.API_RULES.HIT_COUNT', { n: rule.hit_count }) }}
              </StudioBadge>
            </div>
            <div class="flex items-center min-w-0 gap-2">
              <StudioBadge :tone="methodTone(rule.http_method)">
                {{ rule.http_method }}
              </StudioBadge>
              <code class="text-sm truncate text-n-slate-11">
                {{ rule.url }}
              </code>
            </div>
            <p
              v-if="rule.keywords"
              class="truncate text-label-small text-n-slate-10"
            >
              {{ $t('AI_AGENT.API_RULES.TABLE.KEYWORDS') }}:
              {{ rule.keywords }}
            </p>
          </div>
          <div class="flex items-center gap-1 shrink-0">
            <Switch
              :model-value="rule.enabled"
              class="mr-2"
              :data-testid="`rule-enabled-${rule.id}`"
              @update:model-value="toggleEnabled(rule)"
            />
            <Button
              v-tooltip="$t('AI_AGENT.API_RULES.TABLE.TEST')"
              icon="i-lucide-flask-conical"
              slate
              ghost
              xs
              :data-testid="`rule-test-${rule.id}`"
              @click="openTestDialog(rule)"
            />
            <Button
              v-tooltip="$t('AI_AGENT.API_RULES.TABLE.EDIT')"
              icon="i-lucide-pencil"
              slate
              ghost
              xs
              :data-testid="`rule-edit-${rule.id}`"
              @click="openEditDialog(rule)"
            />
            <Button
              v-tooltip="$t('AI_AGENT.API_RULES.TABLE.DELETE')"
              icon="i-woot-bin"
              slate
              ghost
              xs
              class="hover:enabled:text-n-ruby-11 hover:enabled:bg-n-ruby-2"
              :data-testid="`rule-delete-${rule.id}`"
              @click="openDeleteDialog(rule)"
            />
          </div>
        </li>
      </ul>
    </StudioSection>

    <Dialog
      ref="formDialogRef"
      type="edit"
      width="2xl"
      overflow-y-auto
      :title="
        editingRule
          ? $t('AI_AGENT.API_RULES.EDIT_TITLE')
          : $t('AI_AGENT.API_RULES.CREATE_TITLE')
      "
      :show-cancel-button="false"
      :show-confirm-button="false"
    >
      <ApiRuleForm
        :key="editingRule ? editingRule.id : 'new'"
        :rule="editingRule"
        :is-saving="uiFlags.isCreating || uiFlags.isUpdating"
        @save="saveRule"
        @cancel="formDialogRef.close()"
      />
    </Dialog>

    <Dialog
      ref="testDialogRef"
      type="edit"
      width="2xl"
      overflow-y-auto
      :title="$t('AI_AGENT.API_RULES.TEST.TITLE', { name: testingRule?.name })"
      :show-cancel-button="false"
      :show-confirm-button="false"
    >
      <ApiRuleTestPanel
        v-if="testingRule"
        :key="testingRule.id"
        :bot-id="bot.id"
        :rule="testingRule"
      />
    </Dialog>

    <Dialog
      ref="deleteDialogRef"
      type="alert"
      :title="$t('AI_AGENT.API_RULES.DELETE.TITLE')"
      :description="
        $t('AI_AGENT.API_RULES.DELETE.MESSAGE', { name: deletingRule?.name })
      "
      :confirm-button-label="$t('AI_AGENT.API_RULES.DELETE.CONFIRM')"
      :is-loading="uiFlags.isDeleting"
      @confirm="confirmDelete"
    />
  </div>
</template>
